import React, { useState, useEffect } from "react";
import { VendorService, UserService } from "src/services";
import {
  defaultMaxStartDateRangeDate,
  defaultStartDateRangeDate,
  CLAIM_STATUS_OPTIONS,
  CLAIM_TYPE_OPTIONS,
  CLAIM_ISSUE_TYPE_OPTIONS,
  CLAIM_URGENCY_LEVEL_OPTIONS
} from "src/helpers";
import { ClaimsFilters, DateTimeRange } from "src/types";
import { CreationDateSortOptions, ClaimColumnHeader } from "src/enums";
import { LoadingState } from "src/components";
import { MultiSelectInputBox, DropdownField } from "src/components/ui/inputs";
import { Button } from "src/shadcn/ui/button";
import { Input } from "src/shadcn/ui/input";
import { Badge } from "src/shadcn/ui/badge";
import { AlertCircle, Trash2 } from "lucide-react";
import { useAuthorisation } from "src/context";

interface FilterClaimsProps {
  onFilterChange: (filters: ClaimsFilters) => void;
  currentFilter?: ClaimsFilters;
  vendorService?: VendorService;
  userService?: UserService;
  dateTimeRange: DateTimeRange;
  onDateTimeRangeChange: (range: DateTimeRange) => void;
  sortOption: CreationDateSortOptions;
  onSortChange: (option: CreationDateSortOptions) => void;
  columnsToHide: ClaimColumnHeader[];
  onColumnsToHideChange: (columns: ClaimColumnHeader[]) => void;
  allColumns: ClaimColumnHeader[];
  onClose?: () => void;
  isOpen?: boolean;
}

export const FilterClaims: React.FC<FilterClaimsProps> = ({
  onFilterChange,
  currentFilter,
  vendorService,
  userService,
  dateTimeRange,
  onDateTimeRangeChange,
  sortOption,
  onSortChange,
  columnsToHide,
  onColumnsToHideChange,
  allColumns,
  onClose,
  isOpen
}) => {
  const { userRole } = useAuthorisation();
  const isVendor = userRole === "VENDOR";

  const [dropdownOptions, setDropdownOptions] = useState<{
    vendorNames?: string[];
    submittedByEmails?: string[];
    senderEmails?: string[];
  }>({});
  const [loadingDropdownOptions, setLoadingDropdownOptions] = useState(false);
  const [dropdownOptionsFetchError, setDropdownOptionsFetchError] = useState(false);

  const [localDateTimeRange, setLocalDateTimeRange] = useState<DateTimeRange>(dateTimeRange);
  const [localSortOption, setLocalSortOption] = useState<CreationDateSortOptions>(sortOption);

  const [filters, setFilters] = useState<Partial<ClaimsFilters>>({
    statusFilter: currentFilter?.statusFilter
      ? Array.isArray(currentFilter.statusFilter)
        ? currentFilter.statusFilter
        : [currentFilter.statusFilter]
      : undefined,
    vendorName: currentFilter?.vendorName,
    submittedByEmail: currentFilter?.submittedByEmail,
    senderEmail: currentFilter?.senderEmail,
    claimType: currentFilter?.claimType
      ? Array.isArray(currentFilter.claimType)
        ? currentFilter.claimType
        : [currentFilter.claimType]
      : undefined,
    issueType: currentFilter?.issueType
      ? Array.isArray(currentFilter.issueType)
        ? currentFilter.issueType
        : [currentFilter.issueType]
      : undefined,
    urgencyLevel: currentFilter?.urgencyLevel
      ? Array.isArray(currentFilter.urgencyLevel)
        ? currentFilter.urgencyLevel
        : [currentFilter.urgencyLevel]
      : undefined,
    warehouseLocation: currentFilter?.warehouseLocation
  });

  useEffect(() => {
    if (isOpen) {
      setLocalDateTimeRange(dateTimeRange);
      setLocalSortOption(sortOption);
      setFilters({
        statusFilter: currentFilter?.statusFilter
          ? Array.isArray(currentFilter.statusFilter)
            ? currentFilter.statusFilter
            : [currentFilter.statusFilter]
          : undefined,
        vendorName: currentFilter?.vendorName,
        submittedByEmail: currentFilter?.submittedByEmail,
        senderEmail: currentFilter?.senderEmail,
        claimType: currentFilter?.claimType
          ? Array.isArray(currentFilter.claimType)
            ? currentFilter.claimType
            : [currentFilter.claimType]
          : undefined,
        issueType: currentFilter?.issueType
          ? Array.isArray(currentFilter.issueType)
            ? currentFilter.issueType
            : [currentFilter.issueType]
          : undefined,
        urgencyLevel: currentFilter?.urgencyLevel
          ? Array.isArray(currentFilter.urgencyLevel)
            ? currentFilter.urgencyLevel
            : [currentFilter.urgencyLevel]
          : undefined,
        warehouseLocation: currentFilter?.warehouseLocation
      });
    }
  }, [isOpen, dateTimeRange, sortOption, currentFilter]);

  const fetchDropdownOptions = async () => {
    // Don't fetch if userRole is not yet determined
    if (!userRole || userRole === "NONE") return;
    try {
      setLoadingDropdownOptions(true);
      const isVendorUser = userRole === "VENDOR";
      // Don't fetch vendor names for vendors (they only see their own claims)
      const [vendorNames, submittedByEmails, senderEmails] = await Promise.all([
        !isVendorUser ? (vendorService?.getVendorNames() ?? []) : Promise.resolve([]),
        !isVendorUser
          ? (userService?.getUserEmails(["HEALF_OPERATIONS", "HEALF_OPERATIONS_MANAGER", "WAREHOUSE"]) ?? [])
          : Promise.resolve([]),
        !isVendorUser
          ? (userService?.getUserEmails(["HEALF_OPERATIONS", "HEALF_OPERATIONS_MANAGER", "ADMIN"]) ?? [])
          : Promise.resolve([])
      ]);

      setDropdownOptions({
        vendorNames,
        submittedByEmails,
        senderEmails
      });
    } catch (error) {
      setDropdownOptionsFetchError(true);
    } finally {
      setLoadingDropdownOptions(false);
    }
  };

  useEffect(() => {
    fetchDropdownOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  const handleFilterChange = <K extends keyof ClaimsFilters>(key: K, value: ClaimsFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const updatedFilters: ClaimsFilters = {
      ...filters,
      filterPopoverVisible: false
    } as ClaimsFilters;

    onDateTimeRangeChange(localDateTimeRange);
    onSortChange(localSortOption);
    onFilterChange(updatedFilters);
    onClose?.();
  };

  const handleClearAllFilters = () => {
    const clearedFilters: ClaimsFilters = {
      statusFilter: undefined,
      vendorName: undefined,
      submittedByEmail: undefined,
      senderEmail: undefined,
      claimType: undefined,
      issueType: undefined,
      urgencyLevel: undefined,
      warehouseLocation: undefined,
      filterPopoverVisible: false
    };
    setFilters(clearedFilters);

    const defaultRange = { start: defaultStartDateRangeDate(), end: undefined };
    setLocalDateTimeRange(defaultRange);
    onDateTimeRangeChange(defaultRange);

    setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC);
    onSortChange(CreationDateSortOptions.CREATION_DATE_DESC);

    onFilterChange(clearedFilters);
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.statusFilter && (Array.isArray(filters.statusFilter) ? filters.statusFilter.length > 0 : true)) count++;
    if (filters.vendorName) count++;
    if (filters.submittedByEmail) count++;
    if (filters.senderEmail) count++;
    if (filters.claimType && (Array.isArray(filters.claimType) ? filters.claimType.length > 0 : true)) count++;
    if (filters.issueType && (Array.isArray(filters.issueType) ? filters.issueType.length > 0 : true)) count++;
    if (filters.urgencyLevel && (Array.isArray(filters.urgencyLevel) ? filters.urgencyLevel.length > 0 : true)) count++;
    if (filters.warehouseLocation) count++;
    if (localDateTimeRange.start) {
      const defaultStart = defaultStartDateRangeDate();
      const isDefault =
        localDateTimeRange.start.getFullYear() === defaultStart.getFullYear() &&
        localDateTimeRange.start.getMonth() === defaultStart.getMonth() &&
        localDateTimeRange.start.getDate() === defaultStart.getDate() &&
        localDateTimeRange.start.getHours() === defaultStart.getHours() &&
        localDateTimeRange.start.getMinutes() === defaultStart.getMinutes();
      if (!isDefault) count++;
    }
    if (localDateTimeRange.end) count++;
    if (localSortOption !== CreationDateSortOptions.CREATION_DATE_DESC) count++;
    return count;
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    if (!value) {
      const newRange = {
        ...localDateTimeRange,
        [field]: undefined
      };
      setLocalDateTimeRange(newRange);
      return;
    }

    const newDate = new Date(value);
    const maxStartDate = defaultMaxStartDateRangeDate();
    const maxEndDate = new Date();

    if (field === "start") {
      const maxAllowedStart = localDateTimeRange.end ? new Date(localDateTimeRange.end) : maxEndDate;
      const clampedStart =
        newDate < maxStartDate ? maxStartDate : newDate > maxAllowedStart ? maxAllowedStart : newDate;

      setLocalDateTimeRange({
        start: clampedStart,
        end: localDateTimeRange.end && clampedStart > localDateTimeRange.end ? clampedStart : localDateTimeRange.end
      });
    } else {
      const minAllowedEnd = localDateTimeRange.start ? new Date(localDateTimeRange.start) : maxStartDate;
      const clampedEnd = newDate < minAllowedEnd ? minAllowedEnd : newDate > maxEndDate ? maxEndDate : newDate;

      setLocalDateTimeRange({
        ...localDateTimeRange,
        end: clampedEnd
      });
    }
  };

  if (loadingDropdownOptions) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingState type="LOADING" />
      </div>
    );
  }

  if (dropdownOptionsFetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-sm text-secondary-600">Failed to load options</p>
        <Button onClick={fetchDropdownOptions} variant="outline" size="sm">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Filters</h3>
            {countActiveFilters() > 0 && (
              <Badge variant="default" className="bg-secondary-900 text-white text-xs px-1.5 py-0 h-4">
                {countActiveFilters()}
              </Badge>
            )}
          </div>
          {countActiveFilters() > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>

        <div className="space-y-2.5">
          <div className="space-y-1.5">
            <MultiSelectInputBox
              id="status-filter"
              label="Status"
              values={
                Array.isArray(filters.statusFilter)
                  ? filters.statusFilter
                  : filters.statusFilter
                    ? [filters.statusFilter]
                    : []
              }
              onChange={(values) => handleFilterChange("statusFilter", values.length > 0 ? (values as any) : undefined)}
              options={
                isVendor
                  ? CLAIM_STATUS_OPTIONS.filter((status) => status !== "Pending" && status !== "Canceled")
                  : CLAIM_STATUS_OPTIONS
              }
              placeholder="Select statuses..."
              size="sm"
              showBadges={true}
            />
          </div>

          {!isVendor && (
            <DropdownField
              label="Vendor"
              value={filters.vendorName}
              onChange={(value) => handleFilterChange("vendorName", value)}
              options={dropdownOptions.vendorNames || []}
            />
          )}

          {!isVendor && (
            <DropdownField
              label="Submitted By Email"
              value={filters.submittedByEmail}
              onChange={(value) => handleFilterChange("submittedByEmail", value)}
              options={dropdownOptions.submittedByEmails || []}
            />
          )}

          {!isVendor && (
            <DropdownField
              label="Sender Email"
              value={filters.senderEmail}
              onChange={(value) => handleFilterChange("senderEmail", value)}
              options={dropdownOptions.senderEmails || []}
            />
          )}

          <div className="space-y-1.5">
            <MultiSelectInputBox
              id="claim-type-filter"
              label="Claim Type"
              values={
                Array.isArray(filters.claimType) ? filters.claimType : filters.claimType ? [filters.claimType] : []
              }
              onChange={(values) => handleFilterChange("claimType", values.length > 0 ? (values as any) : undefined)}
              options={CLAIM_TYPE_OPTIONS}
              placeholder="Select claim types..."
              size="sm"
              showBadges={true}
            />
          </div>

          <div className="space-y-1.5">
            <MultiSelectInputBox
              id="issue-type-filter"
              label="Issue Type"
              values={
                Array.isArray(filters.issueType) ? filters.issueType : filters.issueType ? [filters.issueType] : []
              }
              onChange={(values) => handleFilterChange("issueType", values.length > 0 ? (values as any) : undefined)}
              options={CLAIM_ISSUE_TYPE_OPTIONS}
              placeholder="Select issue types..."
              size="sm"
              showBadges={true}
            />
          </div>

          <div className="space-y-1.5">
            <MultiSelectInputBox
              id="urgency-level-filter"
              label="Urgency Level"
              values={
                Array.isArray(filters.urgencyLevel)
                  ? filters.urgencyLevel
                  : filters.urgencyLevel
                    ? [filters.urgencyLevel]
                    : []
              }
              onChange={(values) => handleFilterChange("urgencyLevel", values.length > 0 ? (values as any) : undefined)}
              options={CLAIM_URGENCY_LEVEL_OPTIONS}
              placeholder="Select urgency levels..."
              size="sm"
              showBadges={true}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Warehouse Location</label>
            <Input
              value={filters.warehouseLocation || ""}
              onChange={(e) => handleFilterChange("warehouseLocation", e.target.value || undefined)}
              placeholder="Warehouse location"
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - Start
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.start)}
              onChange={(e) => handleDateChange("start", e.target.value)}
              min={formatDateForInput(defaultMaxStartDateRangeDate())}
              max={formatDateForInput(localDateTimeRange.end || new Date())}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - End
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.end)}
              onChange={(e) => handleDateChange("end", e.target.value)}
              min={formatDateForInput(localDateTimeRange.start || defaultMaxStartDateRangeDate())}
              max={formatDateForInput(new Date())}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_DESC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_ASC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_ASC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Oldest
              </button>
            </div>
          </div>
        </div>

        <div className="sticky -bottom-4 pt-3 pb-1 bg-background border-t border-secondary-200 -mx-6 px-6 z-10">
          <Button
            onClick={handleApplyFilters}
            className="w-full bg-secondary-900 hover:bg-secondary-800 h-9 text-sm font-medium text-white"
          >
            Apply Changes
          </Button>
        </div>
      </div>

      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Show/Hide Columns</h3>
          <div className="flex items-center gap-2">
            {columnsToHide.length > 0 && (
              <button
                onClick={() => onColumnsToHideChange([])}
                className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Reset
              </button>
            )}
            <span className="text-xs text-secondary-600 font-medium">
              {allColumns.length - columnsToHide.length}/{allColumns.length}
            </span>
          </div>
        </div>
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
          {allColumns.map((column) => {
            const isVisible = !columnsToHide.includes(column);
            return (
              <button
                key={column}
                onClick={() => {
                  if (isVisible) {
                    onColumnsToHideChange([...columnsToHide, column]);
                  } else {
                    onColumnsToHideChange(columnsToHide.filter((c) => c !== column));
                  }
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all ${
                  isVisible
                    ? "bg-secondary-50 text-secondary-900 hover:bg-secondary-100"
                    : "bg-transparent text-secondary-400 hover:bg-secondary-50"
                }`}
              >
                <span className="font-medium truncate">{column}</span>
                <Badge
                  variant={isVisible ? "default" : "outline"}
                  className={`text-xs px-1.5 py-0 h-4 flex-shrink-0 ml-2 ${isVisible ? "bg-green-500 hover:bg-green-600" : ""}`}
                >
                  {isVisible ? "Visible" : "Hidden"}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
